/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type FormEvent, type ReactElement, useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useNavigate } from '@/hooks/use-navigate';
import debounce from 'lodash.debounce';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search as SearchIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Suggestions from '@/components/search/suggestions';
import { useSearchSuggestions } from '@/hooks/use-search-suggestions';
import { useSuggestionProductImages } from '@/hooks/use-suggestion-product-images';
import { useTransformSearchSuggestions } from '@/hooks/use-transform-search-suggestions';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { getSessionJSONItem, setSessionJSONItem, clearSessionJSONItem } from '@/lib/utils';
import { openShopperAgentAndSendMessage } from '@/components/shopper-agent';
import { validateShopperAgentConfig } from '@/components/shopper-agent/shopper-agent.utils';
import { UITarget } from '@/targets/ui-target';

const RECENT_SEARCH_LIMIT = 5;
const RECENT_SEARCH_KEY = 'recent-search-key';
const RECENT_SEARCH_MIN_LENGTH = 3;

/**
 * The URL parameter the SRP loader reads the query out of
 * (routes/_app.search.tsx:107). It is the ONLY source of truth for what the
 * field shows on arrival — see `urlQuery` below.
 */
const SEARCH_QUERY_PARAM = 'q';

export default function SearchBar(): ReactElement {
    const { t } = useTranslation('header');
    const navigate = useNavigate();
    const config = useConfig();
    const [searchParams] = useSearchParams();
    const inputRef = useRef<HTMLInputElement | null>(null);

    /**
     * The field is seeded from — and re-synced to — the URL, never from
     * transient state (CLAUDE.md Performance & Data rule 12).
     *
     * Before this, the input was UNCONTROLLED (`ref` + `onChange`, no `value`)
     * and `query` started at `''`; the submitted term was read out of
     * `inputRef.current.value`, pushed into the URL, and never read back. The
     * header's `LocationKeyedSearch` wrapper (components/header/index.tsx:89)
     * keys this component on `location.pathname + location.search`, so the very
     * navigation the submit performs REMOUNTS it — a fresh uncontrolled input
     * with no value, i.e. the placeholder. The same emptiness followed from a
     * cold load of the results URL and from back/forward.
     *
     * Reading `q` here fixes all three at once and does not depend on that
     * remount key still existing: the sync effect below catches a `q` change on
     * an instance that survives.
     */
    const urlQuery = searchParams.get(SEARCH_QUERY_PARAM) ?? '';
    const [query, setQuery] = useState(urlQuery);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const queryRef = useRef(query);
    const refetchRef = useRef<() => Promise<void>>(() => Promise.resolve());

    /**
     * Has the shopper touched this field on THIS mount (focus or keystroke)?
     *
     * Gates both the suggestions fetch and the suggestions popover. Without it,
     * seeding `query` from the URL would make the SRP fetch suggestions for its
     * own term on every load and then auto-open the full-width suggestions panel
     * over the results, because `shouldOpenPopover`'s second arm only asks
     * whether the input has a value — a condition that used to be reachable only
     * by typing.
     */
    const [engaged, setEngaged] = useState(false);

    const lastUrlQueryRef = useRef(urlQuery);
    useEffect(() => {
        // Only a genuine `q` change (submit, back/forward, a suggestion link)
        // overwrites the field; a re-render while the shopper is mid-word must
        // not yank their text back to the URL's value.
        if (lastUrlQueryRef.current !== urlQuery) {
            lastUrlQueryRef.current = urlQuery;
            setQuery(urlQuery);
            setEngaged(false);
            setShowSuggestions(false);
        }
    }, [urlQuery]);

    const { data: suggestions, refetch } = useSearchSuggestions({
        q: query,
        // NOTE: getSearchSuggestions ignores `expand` — the parameter is kept
        // for API compatibility but supplies nothing; product entries carry no
        // image (search-3a-comparison.md P2 root cause). Thumbnails are
        // backfilled below via getProducts.
        expand: ['images', 'prices'],
        includeEinsteinSuggestedPhrases: true,
        // `engaged &&` keeps a URL-seeded query from firing a suggestions
        // request nobody asked for on every SRP load.
        enabled: engaged && query.trim().length >= RECENT_SEARCH_MIN_LENGTH,
    });

    // P2 fix: one getProducts call for the suggested ids resolves the images
    // the suggestions payload never carries (see use-suggestion-product-images).
    const suggestionProductIds = useMemo(
        () =>
            (suggestions?.productSuggestions?.products ?? [])
                .map((p) => p.productId)
                .filter((id): id is string => Boolean(id)),
        [suggestions]
    );
    const suggestionProductImages = useSuggestionProductImages(suggestionProductIds);

    const transformedSuggestions = useTransformSearchSuggestions(suggestions, suggestionProductImages);

    useEffect(() => {
        queryRef.current = query;
        refetchRef.current = refetch;
    }, [query, refetch]);

    const saveRecentSearch = useCallback((searchText: string) => {
        let searches = getSessionJSONItem<string[]>(RECENT_SEARCH_KEY) || [];
        searches = searches.filter((savedSearchTerm) => {
            return searchText.toLowerCase() !== savedSearchTerm.toLowerCase();
        });
        searches = [searchText, ...searches].slice(0, RECENT_SEARCH_LIMIT);
        setSessionJSONItem(RECENT_SEARCH_KEY, searches);
    }, []);

    const debouncedRefetch = useMemo(() => {
        return debounce(() => {
            const currentQuery = queryRef.current;
            if (currentQuery.trim().length >= RECENT_SEARCH_MIN_LENGTH) {
                void refetchRef.current();
            }
        }, config.pages.search.suggestionsDebounce);
    }, [config.pages.search.suggestionsDebounce]);

    useEffect(() => {
        if (engaged && query.trim().length >= RECENT_SEARCH_MIN_LENGTH) {
            debouncedRefetch();
        } else {
            debouncedRefetch.cancel();
        }

        return () => {
            debouncedRefetch.cancel();
        };
    }, [query, engaged, debouncedRefetch]);

    const shouldOpenPopover = useCallback(() => {
        // Suggestions are an interaction affordance, never an arrival state.
        if (!engaged) {
            setShowSuggestions(false);
            return;
        }

        const recentSearches = getSessionJSONItem<string[]>(RECENT_SEARCH_KEY) || [];
        const searchSuggestionsAvailable =
            transformedSuggestions &&
            (transformedSuggestions.categorySuggestions.length > 0 ||
                transformedSuggestions.productSuggestions.length > 0 ||
                (transformedSuggestions.popularSearchSuggestions?.length ?? 0) > 0);

        if (
            (document.activeElement === inputRef.current && recentSearches.length > 0) ||
            (searchSuggestionsAvailable && inputRef.current?.value && inputRef.current.value.length > 0)
        ) {
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
    }, [engaged, transformedSuggestions]);

    /** Focus counts as engagement — it is what reveals recent searches. */
    const handleFocus = useCallback(() => {
        setEngaged(true);
        shouldOpenPopover();
    }, [shouldOpenPopover]);

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setEngaged(true);
            setQuery(value);
            shouldOpenPopover();
        },
        [shouldOpenPopover]
    );

    const handleSubmit = useCallback(
        (e: FormEvent) => {
            e.preventDefault();
            if (inputRef.current?.value?.trim()) {
                const searchQuery = inputRef.current.value.trim();
                saveRecentSearch(searchQuery);
                setShowSuggestions(false);
                // Re-submitting the SAME term leaves `q` unchanged, so the sync
                // effect never runs — disengage here so the panel does not stay
                // open over the results in that case.
                setEngaged(false);
                void navigate(`/search?q=${encodeURIComponent(searchQuery)}`, {
                    state: { query: searchQuery },
                });
            }
        },
        [navigate, saveRecentSearch]
    );

    const closeAndNavigate = useCallback(
        (link: string) => {
            inputRef.current?.blur();
            setShowSuggestions(false);
            setEngaged(false);
            // The input is controlled now, so state alone empties it; the
            // former imperative `inputRef.current.value = ''` would only
            // desynchronise React's value tracker.
            setQuery('');
            if (link) {
                void navigate(link);
            }
        },
        [navigate]
    );

    const clearRecentSearches = useCallback(() => {
        clearSessionJSONItem(RECENT_SEARCH_KEY);
        setShowSuggestions(false);
    }, []);

    const showShopperAgent =
        (config.commerceAgent?.enabled === 'true' || config.commerceAgent?.enabled === true) &&
        validateShopperAgentConfig(config.commerceAgent);

    const onShopperAgentClick = useCallback(() => {
        const searchText = inputRef.current?.value?.trim() ?? query.trim();
        setShowSuggestions(false);
        setEngaged(false);
        setQuery('');
        openShopperAgentAndSendMessage(searchText);
    }, [query]);

    useEffect(() => {
        shouldOpenPopover();
    }, [query, suggestions, shouldOpenPopover]);

    return (
        /* NO Radix `Popover` HERE, AND THAT IS THE POINT.
           The field used to be wrapped in `<Popover open={showSuggestions}>` +
           `<PopoverTrigger asChild>`, and the panel below has never been a
           `PopoverContent` (see the note above it). So the Root had no content
           to disclose and the Trigger stamped the input with `aria-controls`
           pointing at a `radix-*` id that is never in the document, plus
           `aria-haspopup`/`aria-expanded` for a dialog that does not exist.
           It was also inert as a control: `open` was passed with no
           `onOpenChange`, so Radix's own trigger click was a no-op.
           Removing it changes no DOM node and no style — the field is matched
           by `input[data-testid='header-search']` in theme/overrides/
           text-field.css, not by the `data-slot` the trigger overwrote. */
        <UITarget targetId="sfcc.header.search.input">
            <>
                <form onSubmit={handleSubmit} className="relative z-10">
                    {/* 3c blast-1 (.reference/harvest/les-blast-1-header-1280.md):
                        live "input+button 409.59x28" = 319.59 input + 90 submit,
                        ZERO gap, right-aligned inside the 512 container — landed
                        as --header-search-* tokens (md+ only; the mobile search
                        row keeps flex-1/gap-2 as before, and BG pins its pre-3c
                        values in brand-bg.css). */}
                    <div className="flex items-center gap-2 md:gap-(--header-search-gap) md:justify-end">
                        <div className="relative flex-1 min-w-0 md:max-w-(--header-search-input-width)">
                            {/* Search field box metrics, border, padding, focus border and the
                                `labelLargeDefault` type role all come from luxe's text field tokens
                                (docs/nm-design-system.md §8.11 / §2.2). They are applied in
                                src/theme/overrides/text-field.css rather than as utilities here,
                                because the Input primitive's own `h-9 px-3 py-1 border-input
                                text-base md:text-sm` would otherwise win or lose by stylesheet
                                order. `pl-10` is clearance for the leading search icon on
                                MOBILE only — live desktop NM has no glass in the input
                                (it lives in the SEARCH submit button; owner, 2026-08-16),
                                so md+ reverts to the field's own padding. */}
                            {/* NO COMBOBOX ARIA. This field carries `role="combobox"`
                                + `aria-autocomplete="list"` + `aria-expanded` +
                                `aria-haspopup="listbox"` no longer, because none of
                                the pattern behind those words was ever implemented:
                                the panel's rows are `<li><a>` with no `role="option"`,
                                nothing sets `aria-activedescendant`, and there is no
                                arrow-key handler on this input or anywhere under it.
                                A screen reader was told "combobox, expanded" and then
                                given nothing to move through, and Down did nothing —
                                WCAG 4.1.2.
                                The fix is deliberately SUBTRACTIVE: adding
                                `role="option"` to the rows without active-descendant
                                tracking and key handling would make the same false
                                promise more specific, not less false. As a plain
                                labelled text input the announcement is now true.
                                If the roving-focus pattern is ever built, the ARIA
                                comes back WITH it, in the same change. */}
                            <Input
                                ref={inputRef}
                                type="text"
                                placeholder={t('searchPlaceholder')}
                                // The trailing class serves `data-query-untouched`
                                // below. Written out literally in ONE string —
                                // the Tailwind scanner reads source text, so a
                                // class composed from a constant lands on the
                                // element and emits no rule at all. The
                                // `inherit` fallback makes it inert for any brand
                                // that does not declare the token.
                                className="w-full pl-10 md:pl-3 md:data-[query-untouched=true]:[color:var(--header-search-submitted-color,inherit)]"
                                // Controlled: the submitted term stays in the
                                // field after a search, a reload of the
                                // results URL and a back/forward between two
                                // queries. Live NM's own mobile SRP header
                                // carries it the same way —
                                // .reference/captures/search-m-375/App.tsx:80
                                // `<input type="text" aria-label="Search Box"
                                // value="dress">`, against home-m-375:80's
                                // `placeholder="Search"` with no value.
                                //
                                // THAT CITATION IS MOBILE-ONLY, AND LIVE SPLITS
                                // BY VIEWPORT. Re-read 2026-08-21 on the element
                                // itself at every captured width, with a nonzero
                                // positive control in the same command:
                                //   search-m-375/390/440   value="dress"
                                //                          aria-label="Search Box"
                                //   search-d-768/1024/1280/1440/1920
                                //                          placeholder="dress"
                                //                          NO value attribute
                                //                          aria-label="Search Box
                                //                          Collapsed"
                                // Corroborated in a second tier that does not
                                // duplicate its page: `.reference/
                                // search-dress.desktop.html` carries
                                // `class="search-box__text" … value=""` with the
                                // term in the placeholder. So on DESKTOP live
                                // paints the submitted term as placeholder ink,
                                // which is why the owner reads ours as "way too
                                // dark" — 2026-08-21, "the searched term sits in
                                // the input way too dark".
                                //
                                // WE KEEP THE VALUE. Emptying the field on
                                // desktop only would need a viewport branch at
                                // render time, which is not SSR-safe here, and
                                // would re-open the three bugs the comment above
                                // records (term lost on submit-remount, on cold
                                // load, and on back/forward). Instead the state
                                // live expresses through an attribute is marked
                                // explicitly, and only that state takes live's
                                // placeholder ink: an untouched field still
                                // showing the URL's term. Type one character and
                                // `engaged` flips, the attribute drops, and the
                                // text is black — which is also what live does
                                // once the field carries a real value.
                                data-query-untouched={
                                    !engaged && query !== '' && query === urlQuery ? 'true' : undefined
                                }
                                value={query}
                                onChange={handleInputChange}
                                onFocus={handleFocus}
                                onBlur={() => setShowSuggestions(false)}
                                aria-label={t('searchPlaceholder')}
                                data-testid="header-search"
                            />
                            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 md:hidden" />
                            {/* CLEAR (✕) — mobile only, and only while the field
                                carries a value, which is exactly live's rule:
                                the control is an `input[type=reset]` that only
                                gets the `exposed-search-reset-btn--shown` class
                                when the box is non-empty.

                                RE-SOURCED 2026-08-19; every value here survived
                                the barred nm-search-m-440 dump that used to be
                                the sole citation.
                                  · The `input[type=reset]` and its box are read
                                    at .reference/captures/search-m-390/
                                    App.tsx:112 — `type="reset"` with
                                    `w-[21px] h-[19px] top-[17px] right-4`.
                                  · The `--shown` modifier is a CLASS NAME, so
                                    it comes from the raw dump per rule 2g:
                                    `.reference/search-dress.desktop.html`
                                    carries `exposed-search-reset-btn--shown`
                                    (positive control `clean-header-section`
                                    fires in the same file). That is live's own
                                    naming for a state modifier, on a page whose
                                    field IS populated. The complementary half —
                                    that it is absent on an EMPTY field — is not
                                    carried by anything we hold; it is inferred
                                    from the modifier's name and is the one
                                    unverified link in this paragraph.

                                BOX AND INSET ARE LIVE'S, and the pixels close
                                the box model. `.reference/live-shots/
                                nm-search-390.png` (sRGB, 2.0 px/CSS px): the
                                field's border box runs x=10.0..381.5 and
                                y=115..147, and the reset glyph's ink runs
                                x=353.0..371.5 with a vertical centre of exactly
                                131.50 against the field's 131. The capture's
                                `top-[17px]` measured from the search row's
                                positioned wrapper at y=105 puts a 19-tall box
                                at 122..141 — centre 131.5, which is what
                                painted. `right-4` there is 16px from the
                                390-wide wrapper, i.e. right edge 374 against
                                the field's 382: the 8px inset this component
                                spells `right-2` against the field itself.

                                GLYPH IS LIVE'S SHAPE, NORMALISED — and the
                                asset is recoverable without the dump, because
                                the capture preserves the base64 inline. Decoded
                                from App.tsx:112 it is a filled circle with a
                                white ✕ knocked out on a `viewBox="0 0 250 250"`
                                — `M250 125c0 58.4-47.4 105.8-105.8 105.8…`
                                plus `path fill="#fff"`. Two deliberate
                                departures, stated
                                rather than absorbed: live's own circle is
                                centred at (144.2,125), not (125,125), so it
                                hangs right and clips the viewBox edge, and
                                live's 21x19 box squashes that circle by ~2px.
                                Both are asset quirks, not design, so the circle
                                is drawn centred in a square 20px box. The ✕ arm
                                proportions are live's (a 250-unit glyph whose
                                bar is ~16.4 wide across a ~100 span).

                                Desktop (md+) is deliberately absent: the only
                                capture of this control is the mobile SRP, and
                                live's desktop field pairs with a separate
                                SEARCH submit whose reset state is unmeasured. */}
                            {query.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setQuery('');
                                        setShowSuggestions(false);
                                        inputRef.current?.focus();
                                    }}
                                    aria-label={t('searchClear', { defaultValue: 'Clear search' })}
                                    data-testid="header-search-clear"
                                    className="absolute right-2 top-1/2 size-5 -translate-y-1/2 cursor-pointer md:hidden">
                                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="size-full">
                                        <circle cx="12" cy="12" r="12" fill="currentColor" />
                                        <path
                                            d="M17.1 8.47L15.53 6.9 12 10.43 8.47 6.9 6.9 8.47 10.43 12 6.9 15.53 8.47 17.1 12 13.57l3.53 3.53 1.57-1.57L13.57 12z"
                                            fill="var(--header-background, #fff)"
                                        />
                                    </svg>
                                </button>
                            )}
                        </div>
                        {/* Visible submit control, desktop only — live NM renders a
                            separate input[submit] beside the search field
                            (.reference/harvest/live-nm-desktop-header-anatomy.json
                            "search submit"). Same form, same /search?q= submit flow;
                            mobile (<md) keeps its icon-only field untouched.
                            3c blast-1 (les-blast-1-header-1280.md "SEARCH button"):
                            90x28 exact — the submit drops the stock Button height
                            and pairs with the 28px input via
                            --header-search-submit-height. */}
                        {/* THE GLASS IS A BACKGROUND, NOT A CHILD — this is live's
                            own mechanism, and swapping to it is what fixes both of
                            the owner's 2026-08-22 notes on this control at once.

                            It used to be `<SearchIcon className="size-4">`, a
                            lucide glyph at 16x16. Live paints ITS OWN magnifier as
                            `background-image` with `background-size:28px 12px` and
                            `background-position:100% 50%`, which letterboxes to
                            12.08 x 12.00 of ink in a 28px lane pinned to the right
                            edge. Two consequences, both reported by the owner:

                              - "our glass icon is too big … our glass circle part
                                is too big"   -> 16x16 lucide vs 12.08x12 live, and
                                a different drawing: live's ring is a 9.37-unit
                                stroke in a 79.73 box where lucide's is 2 in 24.
                                Live's handle CONTACTS the ring; BG's live glyph
                                does not, which is why BG's could not be reused.
                              - "our search button text sits too far left"  -> with
                                the glass as a flex CHILD plus an 8px gap, the label
                                was pushed off-centre by the icon's own box. As a
                                background it occupies no flow at all, and the label
                                is placed purely by live's asymmetric padding.

                            Both values come from live's header bundle; the full
                            citation, the decoded path and the letterboxing
                            arithmetic are on --header-search-submit-glass in
                            theme/tokens/brand-nm/header.css.

                            EVERY ARBITRARY CLASS BELOW IS WRITTEN AS A LITERAL.
                            Tailwind infers usage by scanning source text, so a
                            templated or concatenated class name is never emitted —
                            the rule would simply not exist while the class sat on
                            the element looking correct.

                            `var(..., none)` / `var(..., …)` fallbacks throughout so
                            no shared claim is made for BG: BG's desktop search is a
                            left-panel trigger and never mounts this button, and an
                            unset brand must inherit nothing rather than NM's box. */}
                        <Button
                            type="submit"
                            className={
                                'hidden md:inline-flex w-(--search-submit-width) md:h-(--header-search-submit-height) uppercase bg-(--header-search-submit-bg) ' +
                                'bg-no-repeat [background-image:var(--header-search-submit-glass,none)] ' +
                                '[background-size:var(--header-search-submit-glass-size,auto)] [background-position:100%_50%] ' +
                                '[padding-inline-start:var(--header-search-submit-pad-inline-start,0.75rem)] ' +
                                '[padding-inline-end:var(--header-search-submit-pad-inline-end,0.75rem)] ' +
                                // THE LABEL WAS NOT VERTICALLY CENTRED, and the
                                // cause is that the Button primitive's `py-2`
                                // (8px a side) plus its line box overflow a box
                                // this token pins to live's 28px, so the text
                                // is placed by the overflow rather than by the
                                // box. Live gives its submit `line-height:28px`
                                // and no vertical padding at all, so the line
                                // box IS the button and the glyphs centre
                                // themselves. Measured before/after and the
                                // sources are on --header-search-submit-leading
                                // in tokens/brand-nm/header.css.
                                //
                                // Every fallback restates what this control
                                // renders without the token, so an unset brand
                                // cannot move: `0.5rem` is the primitive's own
                                // `py-2`, and `normal` is the leading it
                                // already resolves. BG never mounts this button
                                // (its desktop search is a left-panel trigger),
                                // so no shared claim is made either way.
                                // `md:` is not decoration. An unprefixed
                                // arbitrary PROPERTY is emitted ahead of the
                                // named utilities in @layer utilities, so a
                                // bare `[padding-block:…]` loses to the
                                // primitive's own `py-2` at equal specificity —
                                // measured: the class landed on the element and
                                // the computed padding stayed 8px. The variant
                                // sorts after, which is the same reason
                                // `md:h-(--header-search-submit-height)` above
                                // beats `h-9`. This control is `hidden
                                // md:inline-flex`, so gating at md costs
                                // nothing.
                                'md:[padding-block:var(--header-search-submit-pad-block,0.5rem)] ' +
                                '[line-height:var(--header-search-submit-leading,normal)] ' +
                                '[letter-spacing:var(--header-search-submit-tracking,var(--typography-label-medium-letter-spacing))] ' +
                                '[font-size:var(--header-search-submit-font-size,0.875rem)]'
                            }
                            data-testid="header-search-submit">
                            {t('searchSubmit', { defaultValue: 'Search' })}
                        </Button>
                    </div>
                </form>
                {/* Note: Using a fixed div instead of PopoverContent because the search
                    suggestions panel is designed to span the full page width while the trigger is input with limited width in the header.
                    Using w-screen on Radix PopoverContent will include the scrollbar width, while the rest of page does not aware of scrollbar width
                    this causing the content in Popover to completely miss aligned with the rest of the layout despite using the same section gutter class.
                    Therefore, we go with traditional div to get control over styling for search result area*/}
                {showSuggestions && (
                    <div
                        className="fixed left-0 right-0 z-50 border-b shadow-[0px_1px_12px_rgba(0,0,0,0.25)] max-h-[min(70vh,32rem)] overflow-y-auto bg-popover text-popover-foreground"
                        style={{ top: 'var(--header-height)' }}
                        // `role="group"`, NOT `role="listbox"`. The panel holds
                        // headings and `<ul><li><a>` rows; it has never held a
                        // single `role="option"`, and a listbox whose children
                        // are not options is an invalid pattern that can hide
                        // those links from assistive tech entirely. `group`
                        // carries the accessible name below without claiming a
                        // navigation model nothing implements.
                        role="group"
                        aria-label={t('searchSuggestions')}>
                        <Suggestions
                            searchSuggestions={transformedSuggestions}
                            recentSearches={getSessionJSONItem<string[]>(RECENT_SEARCH_KEY) || []}
                            closeAndNavigate={closeAndNavigate}
                            clearRecentSearches={clearRecentSearches}
                            showShopperAgent={showShopperAgent}
                            onShopperAgentClick={onShopperAgentClick}
                        />
                    </div>
                )}
            </>
        </UITarget>
    );
}
